import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

// Fee Transfer Count - "No Threshold" model, same shape as Refunds.
const FeeTransferCountDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null); // the real totals the home card also shows
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/fee-transfer-count-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty table saying "No fee transfers this period", which is false
        if (res?.status !== 'success') {
          setRows([]);
          setSummary(null);
          setError(res?.message || 'Could not load Fee Transfer Count');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalCount: res?.data?.totalCount ?? 0, totalValue: res?.data?.totalValue, people: res?.data?.totalEvaluated ?? 0 });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Fee Transfer Count');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const columns = [
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'}>
          {row.entityName}{row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'feeTransferCount', headerName: 'Fee Transfer Count', align: 'right' },
  ];

  return (
    <WarningPage
      title="Fee Transfer Count"
      actions={
        <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
          Back to Warning Dashboard
        </Button>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && summary && (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          <strong>{Number(summary.totalCount).toLocaleString('en-IN')}</strong> {summary.totalCount === 1 ? 'fee transfer' : 'fee transfers'}
          {summary.totalValue != null ? ` · ₹${Number(summary.totalValue).toLocaleString('en-IN')}` : ''} by {summary.people} {summary.people === 1 ? 'person' : 'people'}
        </Typography>
      )}

      {!loading && !error && (
        <WarningMetricTable
          rows={rows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No fee transfers this period."
          footer={(shown) => [
            {
              key: 'total',
              first: true,
              emphasis: true,
              cells: {
                entityName: shown.length === rows.length ? 'Total' : 'Total of the rows shown',
                feeTransferCount: shown.reduce((t, r) => t + Number(r.feeTransferCount || 0), 0).toLocaleString('en-IN'),
              },
            },
          ]}
          exportFileName="Fee_Transfer_Count"
        />
      )}
    </WarningPage>
  );
};

export default FeeTransferCountDashboard;
